import React, { useMemo } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { components } from "../../types/api";
import CampaignDetailsCard from "../card/CampaignDetailsCard";
import MetricsTimelineChart, {
  MetricDataPoint,
  MetricType,
} from "../card/MetricsTimelineChart";
import CreativeMetricsSection from "./CreativeMetricsSection";
import PublisherMetricsSection from "./PublisherMetricsSection";
import {
  cardGridSx,
  costPer,
  exportDailyMetrics,
  MetricsDateToolbar,
  PerformanceTable,
  rowsByCampaign,
  rowsByDay,
  SectionTitle,
} from "./PaidMediaSections";
import {
  formatCurrency,
  formatDecimal,
  formatNumber,
  formatPercentage,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";

type CampaignMetricsDTO = components["schemas"]["CampaignMetricsDTO"];

interface GoogleAdsMergeCampaignDetailsProps {
  isLoadingMergedCampaign: boolean;
  mergedCampaignId: string | undefined;
  mergedCampaignData:
    | components["schemas"]["MergedCampaignMetrics"]
    | null
    | undefined;
  metricsStartDate?: string;
  metricsEndDate?: string;
  onMetricsStartDateChange?: (value: string) => void;
  onMetricsEndDateChange?: (value: string) => void;
  onClearMetricsDateFilters?: () => void;
}

/**
 * Dashboard de cliente de una campaña de Google Ads (Search, Performance Max y
 * Demand Gen). Todo el coste es el de cliente: `cost` ya lleva el margen diario,
 * o es el coste de plataforma cuando la inversión la paga el cliente.
 *
 * Las tarjetas y las tablas salen del resumen y de las filas diarias, que
 * respetan el filtro de fechas. El alcance y los desgloses de anuncios y
 * palabras clave son fotos de toda la campaña, y así se indica.
 */
const GoogleAdsMergeCampaignDetails: React.FC<
  GoogleAdsMergeCampaignDetailsProps
> = ({
  isLoadingMergedCampaign,
  mergedCampaignId,
  mergedCampaignData,
  metricsStartDate,
  metricsEndDate,
  onMetricsStartDateChange,
  onMetricsEndDateChange,
  onClearMetricsDateFilters,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const currency = mergedCampaignData?.currency_enum;
  const campaignStartDate = mergedCampaignData?.startDate
    ? dayjs(mergedCampaignData.startDate).format("YYYY-MM-DD")
    : "";
  const campaignEndDate = mergedCampaignData?.endDate
    ? dayjs(mergedCampaignData.endDate).format("YYYY-MM-DD")
    : "";

  const summary = mergedCampaignData?.metrics?.summary;
  const rawDailyData = mergedCampaignData?.metrics?.dailyData;
  const dailyData = useMemo(() => rawDailyData ?? [], [rawDailyData]);
  const rawMetrics = useMemo<CampaignMetricsDTO[]>(() => {
    const metrics = mergedCampaignData?.metrics as
      | { rawMetrics?: CampaignMetricsDTO[] }
      | undefined;
    return metrics?.rawMetrics ?? [];
  }, [mergedCampaignData?.metrics]);

  const totalCost = summary?.totalCost ?? 0;
  const totalImpressions = summary?.totalImpressions ?? 0;
  const totalClicks = summary?.totalClicks ?? 0;
  const totalConversions = summary?.totalConversions ?? 0;
  const videoViews =
    summary?.totalVideoViews || summary?.totalCompleteViews || 0;
  const hasVideo =
    videoViews > 0 || (summary?.totalFirstQuartileViews ?? 0) > 0;
  const hasReach = summary?.reachScope === "EXACT" && summary.uniqueUsers > 0;
  const hasViewability = (summary?.totalMeasurableImpressions ?? 0) > 0;

  const timelineData: MetricDataPoint[] = useMemo(
    () =>
      dailyData.map((day) => ({
        date: day.date,
        ctr: day.ctr ?? 0,
        cpc: day.cpc ?? 0,
        cpm: day.cpm ?? 0,
        impressions: day.impressions ?? 0,
        clicks: day.clicks ?? 0,
        cost: day.cost ?? 0,
        completeViews: day.completeViews ?? 0,
        vtr: day.vtr ?? 0,
      })),
    [dailyData]
  );
  const hiddenChartMetrics = useMemo<MetricType[]>(
    () => (hasVideo ? [] : ["completeViews", "vtr"]),
    [hasVideo]
  );

  const campaignRows = useMemo(() => rowsByCampaign(rawMetrics), [rawMetrics]);
  const dailyRows = useMemo(
    () => rowsByDay(rawMetrics, (date) => dayjs(date).format("DD/MM/YYYY")),
    [rawMetrics]
  );

  const notAvailable = t("common.notAvailable", { defaultValue: "N/A" });
  const money = (value: number | null) =>
    value === null ? notAvailable : formatCurrency(value, currency);

  if (isLoadingMergedCampaign) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const paperSx = {
    p: 3,
    borderRadius: 2,
    border: `1px solid ${theme.palette.grey[200]}`,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Paper elevation={0} sx={paperSx}>
        <MetricsDateToolbar
          startDate={metricsStartDate ?? ""}
          endDate={metricsEndDate ?? ""}
          campaignStartDate={campaignStartDate}
          campaignEndDate={campaignEndDate}
          onStartDateChange={onMetricsStartDateChange}
          onEndDateChange={onMetricsEndDateChange}
          onClear={onClearMetricsDateFilters}
          onExport={() =>
            exportDailyMetrics(
              dailyData,
              mergedCampaignData?.name || "google-ads",
              t,
              dispatch
            )
          }
          exportDisabled={dailyData.length === 0}
        />

        <Divider sx={{ mb: 3 }} />

        {!summary || totalImpressions === 0 ? (
          <Typography variant="body1" color="text.secondary">
            {t("campaigns.noMetricsData", {
              defaultValue: "No metrics available for this campaign.",
            })}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Box>
              <SectionTitle
                title={t("campaigns.googleAds.performance", {
                  defaultValue: "Performance",
                })}
              />
              <Box sx={cardGridSx}>
                <CampaignDetailsCard
                  value={formatCurrency(totalCost, currency)}
                  label={t("home.dashboardInvestment", {
                    defaultValue: "Investment",
                  })}
                />
                <CampaignDetailsCard
                  value={formatNumber(totalImpressions)}
                  label={t("campaigns.impressions", {
                    defaultValue: "Impressions",
                  })}
                />
                <CampaignDetailsCard
                  value={formatNumber(totalClicks)}
                  label={t("campaigns.clicks", { defaultValue: "Clicks" })}
                />
                <CampaignDetailsCard
                  value={formatPercentage(summary.avgCTR)}
                  label={t("campaigns.ctr", { defaultValue: "CTR" })}
                />
                <CampaignDetailsCard
                  value={money(costPer(totalCost, totalClicks))}
                  label={t("campaigns.googleAds.avgCpc", {
                    defaultValue: "Avg. CPC",
                  })}
                />
                <CampaignDetailsCard
                  value={formatCurrency(summary.avgCPM, currency)}
                  label={t("campaigns.cpm", { defaultValue: "CPM" })}
                />
              </Box>
            </Box>

            <Box>
              <SectionTitle
                title={t("campaigns.googleAds.conversions", {
                  defaultValue: "Conversions",
                })}
                hint={t("campaigns.googleAds.conversionsHint", {
                  defaultValue:
                    "Conversions as counted by Google Ads for the conversion actions included in the campaign goals.",
                })}
              />
              <Box sx={cardGridSx}>
                <CampaignDetailsCard
                  value={formatNumber(totalConversions)}
                  label={t("campaigns.totalConversions", {
                    defaultValue: "Conversions",
                  })}
                />
                <CampaignDetailsCard
                  value={formatPercentageNoMultiply(
                    summary.conversionRate ?? 0
                  )}
                  label={t("campaigns.conversionRate", {
                    defaultValue: "Conversion Rate",
                  })}
                />
                <CampaignDetailsCard
                  value={money(costPer(totalCost, totalConversions))}
                  label={t("campaigns.googleAds.costPerConversion", {
                    defaultValue: "Cost / conv.",
                  })}
                />
              </Box>
            </Box>

            {hasVideo && (
              <Box>
                <SectionTitle
                  title={t("campaigns.googleAds.video", {
                    defaultValue: "Video",
                  })}
                />
                <Box sx={cardGridSx}>
                  <CampaignDetailsCard
                    value={formatNumber(videoViews)}
                    label={t("campaigns.videoViews", {
                      defaultValue: "Video views",
                    })}
                  />
                  <CampaignDetailsCard
                    value={formatPercentageNoMultiply(summary.avgVTR)}
                    label={t("campaigns.vtr", { defaultValue: "VTR" })}
                  />
                  <CampaignDetailsCard
                    value={money(costPer(totalCost, videoViews))}
                    label={t("campaigns.googleAds.costPerView", {
                      defaultValue: "Cost / view",
                    })}
                  />
                  <CampaignDetailsCard
                    value={formatNumber(summary.totalCompleteViews)}
                    label={t("campaigns.completeViews", {
                      defaultValue: "Complete Views",
                    })}
                  />
                </Box>
              </Box>
            )}

            {(hasReach || hasViewability) && (
              <Box>
                <SectionTitle
                  title={t("campaigns.googleAds.audience", {
                    defaultValue: "Reach and viewability",
                  })}
                  hint={
                    hasReach
                      ? t("campaigns.googleAds.reachHint", {
                          defaultValue:
                            "Reach and frequency cover the whole campaign: unique users cannot be added up across days.",
                        })
                      : undefined
                  }
                />
                <Box sx={cardGridSx}>
                  {hasReach && (
                    <CampaignDetailsCard
                      value={formatNumber(summary.uniqueUsers)}
                      label={t("campaigns.reach", { defaultValue: "Reach" })}
                    />
                  )}
                  {hasReach && summary.frequency != null && (
                    <CampaignDetailsCard
                      value={formatDecimal(summary.frequency)}
                      label={t("campaigns.frequency", {
                        defaultValue: "Frequency",
                      })}
                    />
                  )}
                  {hasViewability && (
                    <CampaignDetailsCard
                      value={formatPercentage(summary.avgViewability ?? 0)}
                      label={t("campaigns.viewability", {
                        defaultValue: "Viewability",
                      })}
                    />
                  )}
                </Box>
              </Box>
            )}

            <MetricsTimelineChart
              title={t("campaigns.metricsOverTime", {
                defaultValue: "Metrics Over Time",
              })}
              subtitle={t("campaigns.clickMetricsToCompare", {
                defaultValue:
                  "Click on metrics to compare or select 'All' to see accumulated data",
              })}
              data={timelineData}
              isLoading={isLoadingMergedCampaign}
              hiddenMetrics={hiddenChartMetrics}
            />
          </Box>
        )}
      </Paper>

      {campaignRows.length > 0 && (
        <Paper elevation={0} sx={paperSx}>
          <SectionTitle
            title={t("campaigns.googleAds.byCampaign", {
              defaultValue: "Performance by campaign",
            })}
          />
          <PerformanceTable
            rows={campaignRows}
            firstColumn={t("campaigns.googleAds.campaign", {
              defaultValue: "Campaign",
            })}
            currency={currency}
            showType
            showVideoViews={hasVideo}
          />
        </Paper>
      )}

      {dailyRows.length > 0 && (
        <Paper elevation={0} sx={paperSx}>
          <SectionTitle
            title={t("campaigns.googleAds.daily", {
              defaultValue: "Daily performance",
            })}
          />
          <PerformanceTable
            rows={dailyRows}
            firstColumn={t("common.date", { defaultValue: "Date" })}
            currency={currency}
            showVideoViews={hasVideo}
          />
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
        {t("campaigns.googleAds.lifetimeHint", {
          defaultValue:
            "Ads, keywords and placements show the totals of the whole campaign, not the selected dates.",
        })}
      </Typography>

      <CreativeMetricsSection
        mergedCampaignId={mergedCampaignId}
        campaignName={mergedCampaignData?.name}
        currency={currency}
        startDate={campaignStartDate}
        endDate={campaignEndDate}
        title={t("campaigns.googleAds.ads", {
          defaultValue: "Ads and asset groups",
        })}
      />

      <PublisherMetricsSection
        mergedCampaignId={mergedCampaignId}
        campaignName={mergedCampaignData?.name}
        currency={currency}
        startDate={campaignStartDate}
        endDate={campaignEndDate}
        title={t("campaigns.googleAds.keywords", {
          defaultValue: "Keywords and placements",
        })}
      />
    </Box>
  );
};

export default GoogleAdsMergeCampaignDetails;
